import React, { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { AnnouncementStatusType, AnnouncementType } from '@golden/gdk-admin'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import DropDown from '../../default/form/DropDown'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import allRoute from '../route/route'
import announcementStatusName from '../../../constants/admin/announcementStatusName'
import useT from '../../../i18ns/admin/useT'
import { useChecker } from '../../../utils/admin/AdminRouteHook'
import { useRequestFromSearch, useChangeUrlSubmit } from '../../../utils/default/ComplexFlowHook'
import { searchToRequest, RequestType } from '../../../views/admin/forestage/AnnouncementPage'
import announcementTypeName from '../../../constants/default/announcementTypeName'
import { isUndefined, omitBy } from '@golden/utils'
import { TextField } from '@material-ui/core'
import { useInternValue } from '../../../utils/default/FormHook'
import { convertEmptyToUndefined } from '../../../utils/default/FormHelper'

const AnnouncementListToolbar: React.FC = () => {
  const request = useRequestFromSearch({ searchToRequest })
  const classes = useCommonStyles()
  const { t } = useT()
  const statusOptions = useMemo(() => {
    return [
      { name: t('common.allStatus'), value: 0 },
      ...([AnnouncementStatusType.OUT_DATE, AnnouncementStatusType.START, AnnouncementStatusType.STOP] as const)
        .map((key) => ({ name: t(announcementStatusName[key]), value: key }))
    ]
  }, [t])
  const typeOptions = useMemo(() => {
    return [
      { name: t('common.all'), value: '--' },
      ...([AnnouncementType.SYSTEM, AnnouncementType.GAME, AnnouncementType.FINANCE, AnnouncementType.ACTIVITY] as const)
        .map((key) => ({ name: t(announcementTypeName[key]), value: key }))
    ]
  }, [t])

  const handleStatusSubmit = useChangeUrlSubmit<{ status: 0 | AnnouncementStatusType }, RequestType>({
    formToRequest: (form) => {
      const converted = {
        status: form.status === 0 ? undefined : form.status,
        type: (!request?.type || request?.type === '--') ? undefined : request?.type,
        content: request?.content,
        page: 1
      } as RequestType
      return omitBy(converted, isUndefined) as RequestType
    },
    encodePath: allRoute.forestageAnnouncement.encodePath,
    toAddNowTimestamp: true
  })

  const handleTypeSubmit = useChangeUrlSubmit<{ type: '--' | AnnouncementType }, RequestType>({
    formToRequest: (form) => {
      const converted = {
        status: request?.status === 0 ? undefined : request?.status,
        type: form.type === '--' ? undefined : form.type,
        content: request?.content,
        page: 1
      } as RequestType
      return omitBy(converted, isUndefined) as RequestType
    },
    encodePath: allRoute.forestageAnnouncement.encodePath,
    toAddNowTimestamp: true
  })

  const [content, setContent] = useInternValue<string>('', request?.content ?? '')
  const handleContentSubmit = useChangeUrlSubmit<{}, RequestType>({
    formToRequest: () => {
      const converted = {
        status: request?.status === 0 ? undefined : request?.status,
        type: (!request?.type || request?.type === '--') ? undefined : request?.type,
        content: convertEmptyToUndefined(content),
        page: 1
      } as RequestType
      return omitBy(converted, isUndefined) as RequestType
    },
    encodePath: allRoute.forestageAnnouncement.encodePath,
    toAddNowTimestamp: true
  })
  const writable = useChecker()

  return (
    <Grid container direction="row" justifyContent="flex-end" spacing={2}>
      <Grid item xs={6} md={2}>
        <DropDown
          className={classes.outlineDropDown}
          fullWidth
          variant="outlined"
          label=""
          value={request?.status ?? 0}
          onChange={useCallback((event) => {
            const status = event.target.value as number
            handleStatusSubmit({ status })
          }, [handleStatusSubmit])}
          options={statusOptions}
        />
      </Grid>
      <Grid item xs={6} md={2}>
        <DropDown
          className={classes.outlineDropDown}
          fullWidth
          variant="outlined"
          label=""
          value={request?.type ?? '--'}
          onChange={useCallback((event) => {
            const type = event.target.value as ('--' | AnnouncementType)
            handleTypeSubmit({ type })
          }, [handleTypeSubmit])}
          options={typeOptions}
        />
      </Grid>
      <Grid item xs={6} md={2}>
        <TextField
          fullWidth
          variant="outlined"
          label={t('common.titleContent')}
          placeholder={t('common.fuzzySearch')}
          value={content}
          onChange={useCallback((event) => {
            setContent(event.target.value)
          }, [])}
        />
      </Grid>
      <Grid item>
        <Button
          className={classes.purpleGradualButton}
          onClick={handleContentSubmit}
        >
          <Box paddingY={0.5}>
            {t('common.search')}
          </Box>
        </Button>
      </Grid>
      {writable && (
        <Grid item>
          <Button
            component={Link}
            to={allRoute.forestageAnnouncementCreate.encodePath({ search: {}, param: {} })}
            className={classes.purpleGradualButton}
            classes={{ disabled: classes.disabledButton }}
          >
            <Box paddingX={2.5} paddingY={0.5}>
              {t('common.createAnnouncement')}
            </Box>
          </Button>
        </Grid>
      )}
    </Grid>
  )
}

export default React.memo(AnnouncementListToolbar)
